import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import { environment } from '../config/environment';
import { cacheManager } from '../utils/cacheManager';

export class BaseApiService {
  protected axiosInstance: AxiosInstance;

  constructor(baseURL?: string) {
    this.axiosInstance = axios.create({
      baseURL: baseURL || environment.apiUrl,
      timeout: 120000, // Increased to 2 minutes for slow Azure operations
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await axios.post(
                `${environment.apiUrl}/api/auth/refresh-token`,
                { refreshToken }
              );

              const { accessToken } = response.data;
              localStorage.setItem('accessToken', accessToken);

              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
          console.error('Access forbidden:', error.response.data);
        }

        // Handle 503 Service Unavailable
        if (error.response?.status === 503) {
          message.info({
            content: 'The system is waking up from idle mode. Please refresh the page in 10-15 seconds.',
            duration: 8,
            key: 'service-unavailable',
          });
        }

        // Handle 502 Bad Gateway
        if (error.response?.status === 502) {
          message.info({
            content: 'The system is starting up. Please refresh the page in a few seconds.',
            duration: 8,
            key: 'bad-gateway',
          });
        }

        // Handle network errors (no response)
        if (!error.response && error.code === 'ERR_NETWORK') {
          message.warning({
            content: 'Unable to reach the server. Please check your internet connection and try again.',
            duration: 5,
            key: 'network-error',
          });
        }

        return Promise.reject(error);
      }
    );
  }

  protected async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    useCache: boolean = true
  ): Promise<T> {
    const cacheKey = cacheManager.generateCacheKey(url, config?.params);

    // Check cache
    if (useCache) {
      const cachedData = cacheManager.get(cacheKey);
      if (cachedData) {
        if (environment.debug) {
          console.log(`[BaseApiService] Cache hit for: ${url}`);
        }
        return cachedData;
      }

      // Check for pending request (deduplication)
      const pendingRequest = cacheManager.getPendingRequest(cacheKey);
      if (pendingRequest) {
        if (environment.debug) {
          console.log(`[BaseApiService] Deduplicating request for: ${url}`);
        }
        return pendingRequest;
      }
    }

    // Make request
    const requestPromise = this.axiosInstance
      .get<T>(url, config)
      .then((response: AxiosResponse<T>) => {
        if (useCache) {
          cacheManager.set(cacheKey, response.data, url);
        }
        return response.data;
      })
      .catch((error) => {
        throw error;
      });

    if (useCache) {
      cacheManager.setPendingRequest(cacheKey, requestPromise);
    }

    return requestPromise;
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  public clearCache(): void {
    cacheManager.clear();
  }
}
